import { Header } from '../Header';
import { t, type TranslationKey } from '../i18n/translations';
import './EmergencyBookingView.css';

interface EmergencyBookingViewProps {
  problems: string[];
  selectedProblems: string[];
  selectedRegion: string[];
  canContinue: boolean;
  onToggleProblem: (problem: string) => void;
  onSelectRegion: (vertical: string, horizontal: string) => void;
  onBack: () => void;
  onNext: () => void;
}

const region = { left: 'left', right: 'right', upper: 'upper', lower: 'lower' } as const;

const additionalProblem = 'Beschwerden beim Zahnfleisch/Lippe/Wange';

export function EmergencyBookingView({
  problems, selectedProblems, selectedRegion, canContinue, onToggleProblem, onSelectRegion, onBack, onNext,
}: EmergencyBookingViewProps) {
  return (
    <div className="emergency-booking min-h-screen">
      <Header compact />

      <main className="emergency-booking__content" aria-labelledby="emergency-booking-title">
        <div className="emergency-booking__intro">
          <span className="emergency-booking__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor" focusable="false">
              <path d="M10 2h4v6.5l5.6-3.2 2 3.4L16 12l5.6 3.3-2 3.4-5.6-3.2V22h-4v-6.5l-5.6 3.2-2-3.4L8 12 2.4 8.7l2-3.4L10 8.5Z" />
            </svg>
          </span>
          <div>
            <h1 id="emergency-booking-title">Notfalltermin</h1>
            <p>Beschreiben Sie Ihre Beschwerden, damit sich das Praxisteam optimal vorbereiten kann.</p>
          </div>
        </div>

        <section className="emergency-booking__card" aria-labelledby="emergency-problems-title">
          <h2 id="emergency-problems-title">Wählen Sie Ihre Beschwerden aus</h2>
          <p className="emergency-booking__supporting">Mehrfachauswahl möglich</p>
          <div className="emergency-booking__problems">
            {[...problems, additionalProblem].map((problem) => (
              <button
                key={problem}
                type="button"
                aria-pressed={selectedProblems.includes(problem)}
                onClick={() => onToggleProblem(problem)}
              >
                {problem === 'dental pain' ? 'Schmerzen' : t(problem as TranslationKey)}
              </button>
            ))}
          </div>
        </section>

        <section className="emergency-booking__card emergency-booking__region-card" aria-labelledby="emergency-region-title">
          <h2 id="emergency-region-title">Wählen Sie die betroffene Region aus</h2>
          <p className="emergency-booking__supporting">
            Klicken Sie auf die Region, die Beschwerden verursacht. Geben Sie die Seite (links/rechts) aus Ihrer Perspektive an.
          </p>

          <div className="emergency-booking__diagram">
            <img
              src="/images/emergency/dental-regions.png"
              alt="Ober- und Unterkiefer zur Auswahl der betroffenen Region"
              className="block aspect-[16/9] w-full object-contain"
            />

            <span className="pointer-events-none absolute left-1/2 top-[2%] -translate-x-1/2 text-[11px] text-black">{t(region.upper)}</span>
            <span className="pointer-events-none absolute bottom-[2%] left-1/2 -translate-x-1/2 text-[11px] text-black">{t(region.lower)}</span>
            <span className="pointer-events-none absolute left-[1%] top-1/2 -translate-y-1/2 text-[11px] capitalize text-black">{t(region.right)}</span>
            <span className="pointer-events-none absolute right-[1%] top-1/2 -translate-y-1/2 text-[11px] capitalize text-black">{t(region.left)}</span>

            {([
              { vertical: region.upper, horizontal: region.right, className: 'left-[10%] top-[14%] h-[34%] w-[40%]' },
              { vertical: region.upper, horizontal: region.left, className: 'right-[10%] top-[14%] h-[34%] w-[40%]' },
              { vertical: region.lower, horizontal: region.right, className: 'bottom-[14%] left-[10%] h-[34%] w-[40%]' },
              { vertical: region.lower, horizontal: region.left, className: 'bottom-[14%] right-[10%] h-[34%] w-[40%]' },
            ]).map((regionOption) => {
              const selected = selectedRegion[0] === regionOption.vertical && selectedRegion[1] === regionOption.horizontal;
              return (
                <button
                  key={`${regionOption.vertical}-${regionOption.horizontal}`}
                  type="button"
                  aria-label={`${t(regionOption.vertical)}, ${t(regionOption.horizontal)}`}
                  aria-pressed={selected}
                  onClick={() => onSelectRegion(regionOption.vertical, regionOption.horizontal)}
                  className={`absolute rounded-xl border-2 transition ${regionOption.className} ${selected
                    ? 'border-[#43a7bd] bg-[#5fc3dc]/25 shadow-[0_0_0_2px_rgba(255,255,255,0.5)]'
                    : 'border-transparent hover:border-[#8ccbd8]/70 hover:bg-[#7ed5e5]/10'
                    }`}
                />
              );
            })}
          </div>

          <div aria-live="polite" aria-atomic="true">
            {selectedRegion.length > 0 ? (
              <p className="emergency-booking__selection">
                {`Ausgewählter Bereich: Ich habe Beschwerden im ${t(selectedRegion[0] as TranslationKey)} auf meiner ${selectedRegion[1] === 'right' ? 'rechten' : 'linken'} Seite.`}
              </p>
            ) : null}
          </div>
        </section>

        <div className="emergency-booking__actions">
          <button type="button" onClick={onBack} className="emergency-booking__back">
            <span aria-hidden="true">←</span> Zurück
          </button>
          <button type="button" onClick={onNext} disabled={!canContinue} className="emergency-booking__next">
            Weiter <span aria-hidden="true">→</span>
          </button>
        </div>
      </main>
    </div>
  );
}
